import { useState } from "react";
import { uomOptions, validateUomCode, normalizeUomCode } from "../utils/uoms";

// Unit-of-measure picker for Item / Assembly forms, sourced from the admin
// catalog (db/81 EstimateUnitsOfMeasure) instead of a hardcoded list.
//
//   value     — the row's current UOM code ('ea')
//   onChange  — (code) => void
//   uoms      — sp_GetEstimateUnitsOfMeasure rows (active ones offered; the
//               row's own value is always offered too, see uomOptions)
//   onAddNew  — optional async (code) => Promise<boolean>. When supplied a
//               "＋ New unit…" entry is appended: picking it prompts for a code,
//               hands it to onAddNew (which saves + reloads the catalog) and
//               selects it on success. Pass it only where the user is an
//               admin (the Estimate Engine page); the estimator-facing
//               add-new flyout leaves it out and gets a plain list.
const ADD_NEW = "__add_new_uom__";

export function UomSelect({ value, onChange, uoms = [], onAddNew, style, disabled = false }) {
  const [busy, setBusy] = useState(false);
  const options = uomOptions(uoms, value);
  const current = normalizeUomCode(value);

  async function handleChange(e) {
    const v = e.target.value;
    if (v !== ADD_NEW) { onChange?.(v); return; }
    const typed = window.prompt('New unit code (e.g. "cfm", "ls", "lb/hr"):', "");
    if (typed == null) return;
    const err = validateUomCode(typed);
    if (err) { window.alert(err); return; }
    const code = normalizeUomCode(typed);
    setBusy(true);
    try {
      const ok = await onAddNew(code);
      if (ok !== false) onChange?.(code);
    } finally { setBusy(false); }
  }

  return (
    <select value={current} onChange={handleChange} style={style} disabled={disabled || busy} title={options.find(o => o.code === current)?.label || ""}>
      {/* A blank current value (a brand-new assembly draft can carry "") still
          needs a matching option or the browser shows the first entry while
          the draft actually holds "". */}
      {!current && <option value="">UOM —</option>}
      {options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
      {onAddNew && <option value={ADD_NEW}>{busy ? "Saving…" : "＋ New unit…"}</option>}
    </select>
  );
}

export default UomSelect;
